import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import { scrollToTarget } from "@/App";

const HERO_IMG =
  "https://images.pexels.com/photos/6322360/pexels-photo-6322360.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function RotatingBadge() {
  return (
    <div className="absolute -top-8 -right-6 sm:-right-10 z-10 hidden sm:block">
      <div className="relative h-32 w-32">
        <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
          <defs>
            <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
          </defs>
          <text className="fill-zinc-400 font-mono" style={{ fontSize: "8.2px", letterSpacing: "2.6px" }}>
            <textPath href="#circlePath">
              CREATIVITÀ APPLICATA • MILANO •
            </textPath>
          </text>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-display font-extrabold text-ember text-xl">G</span>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 100]);
  const yWord = useTransform(scrollY, [0, 700], [0, -70]);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-[72px]">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <div className="absolute inset-0 hero-glow" aria-hidden="true" />

      <motion.span
        style={{ y: yWord }}
        aria-hidden="true"
        className="text-outline font-display font-extrabold absolute -bottom-6 left-0 whitespace-nowrap text-[19vw] leading-none select-none pointer-events-none"
      >
        CREATIVITÀ
      </motion.span>

      <div className="relative max-w-[1400px] w-full mx-auto px-6 sm:px-10 pt-24 lg:pt-28 pb-16 lg:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="text-xs uppercase tracking-[0.3em] text-ember font-mono mb-8 flex items-center gap-3"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-ember" aria-hidden="true" />
          Giango — Il primo studio di creatività applicata
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display font-extrabold tracking-tight leading-[1.04] text-[clamp(1.65rem,5.9vw,4rem)] text-ink"
        >
          <MaskedLine delay={0.45}>Se vuoi comunicare</MaskedLine>
          <MaskedLine delay={0.56}>efficacemente</MaskedLine>
          <MaskedLine delay={0.67}>il tuo messaggio,</MaskedLine>
          <MaskedLine delay={0.78} className="text-ember italic">
            devi metterci l'anima.
          </MaskedLine>
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-end mt-6 lg:mt-10">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
              className="max-w-xl text-lg text-zinc-400 leading-relaxed"
            >
              Creatività applicata alla comunicazione e alla formazione:
              progetto soluzioni ad hoc sulle esigenze della tua azienda,
              oppure attivo format già collaudati del mio portafoglio.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-explore"
                onClick={() => scrollToTarget("#servizi")}
                className="group inline-flex items-center gap-2.5 bg-ember text-obsidian font-semibold px-7 py-4 rounded-full text-base hover:bg-ember-light transition-all duration-300 hover:gap-4"
              >
                Esplora i servizi
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                data-testid="hero-cta-contact"
                onClick={() => scrollToTarget("#contatti")}
                className="group inline-flex items-center gap-2.5 border border-white/20 text-ink font-semibold px-7 py-4 rounded-full text-base hover:border-ember hover:text-ember transition-all duration-300"
              >
                Richiedi un progetto ad hoc
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </motion.div>
          </div>

          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
            className="lg:col-span-6 relative"
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[16/10] max-w-xl mx-auto lg:ml-auto lg:mr-0">
              <img
                src={HERO_IMG}
                alt="Giango al lavoro in studio"
                className="h-full w-full object-cover img-duotone"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                  Giango Studio — Milano
                </p>
                <span className="font-mono text-[10px] text-ember">EST. CREATIVITÀ</span>
              </div>
            </div>
            <RotatingBadge />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Scorri</span>
        <span className="block h-10 w-px bg-ember animate-scroll-hint" aria-hidden="true" />
      </motion.div>
    </section>
  );
}
